// Certificado de Inversión — generador HTML.
//
// Réplica del PDF de referencia que la cooperativa ya emite (formato
// "cdp fin test.pdf"). 2 páginas A4 landscape (297×210mm). Modality-aware
// (MENSUAL vs AL_VENCIMIENTO).
//
// Sin dependencias de runtime más allá de string-templating. Trabajable
// desde RN (expo-print) y desde el browser (window.print / iframe).

import type { CertAsociado, CertPlazoFijo } from './types'
import { LOGO_HORIZONTAL } from './assets/logo'
import { SEAL_GOLD_PNG } from './assets/seal'

import { renderQrSvg } from './qr'

/* ---------------- datos institucionales ---------------- */

const COOP_NOMBRE = 'Cooperativa Integral de Ahorro y Crédito NEO, R.L.'
const COOP_NIT = '120719290'
const COOP_EMAIL = 'info@neocoop.gt'
const COOP_FISCAL_ADDR =
  'El Cubo Center, Oficina 209, Km 77 Ruta Nacional 14, San Lorenzo El Cubo, Ciudad Vieja, Sacatepéquez, Guatemala.'

const PLAZO_MESES_NUM: Record<number, number> = {
  180: 6,
  365: 12,
  545: 18,
  730: 24,
}

/* ---------------- helpers ---------------- */

const esc = (s: string): string =>
  s.replace(
    /[&<>"]/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string,
  )

const fmtFechaCert = (iso: string): string => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`)
  if (isNaN(d.getTime())) return '—'
  const mes = [
    'ENE','FEB','MAR','ABR','MAY','JUN',
    'JUL','AGO','SEP','OCT','NOV','DIC',
  ][d.getMonth()]
  return `${String(d.getDate()).padStart(2, '0')}/${mes}/${d.getFullYear()}`
}

const fmtQ = (n: number): string =>
  `Q.${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

/* ---------------- CSS ---------------- */

const CERT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600&display=swap');
/* A4 landscape — 297×210mm. El cert se imprime en hoja A4 estándar
   (lo que se consigue en cualquier librería en Guatemala). Antes era
   Letter pero A4 es el formato físico que va a usar el operador. */
/* @page: forzamos dimensiones explícitas en mm (no 'A4 landscape' a secas)
   porque Chrome a veces ignora el keyword y deja la hoja en portrait,
   lo cual parte un CDP de 2 hojas landscape en 3 hojas portrait.
   297mm × 210mm = A4 landscape sin ambigüedad. */
@page{ size:297mm 210mm; margin:0; }
:root{
  /* Brand oficial neocoop — alineado con admin/src/styles.css */
  --brand:#FF5B04; --brand-deep:#c43c00; --brand-soft:#ffe4d4;
  --ink:#14171c; --ink-soft:#4b5563; --muted:#9aa0a6;
  --line:#e5e7eb; --line-soft:#f3f4f6;
  --cream:#fffbf2; --paper-white:#ffffff;
}
*{box-sizing:border-box;}
html,body{margin:0;padding:0;}
body{
  font-family:'Inter',-apple-system,BlinkMacSystemFont,system-ui,Arial,sans-serif;
  color:var(--ink);
  /* Fondo gris claro en pantalla para que las hojas A4 "floten" centradas
     (look tipo Google Docs / preview). En impresión se resetea. */
  background:#e5e7eb;
  -webkit-print-color-adjust:exact; print-color-adjust:exact;
}

.page{
  /* A4 landscape físico: 297mm × 210mm.
     Padding interno asimétrico — top/left/right 12mm, BOTTOM 20mm:
     las impresoras de inyección (EPSON L5590, Canon, HP) tienen un
     "área no imprimible" físico de ~12-15mm en el borde inferior. Si el
     contenido llega cerca del borde, la impresora lo recorta físicamente.
     Los 20mm bottom + 12mm top = 32mm padding vertical absorben esa zona
     muerta sin perder casi espacio útil (antes 30mm).
     EN PANTALLA: min-height para que el contenido fluya sin cortarse. */
  width:297mm; min-height:210mm; padding:12mm 15mm 20mm 15mm;
  position:relative; page-break-after:always;
  display:flex; flex-direction:column;
  margin:24px auto;
  box-shadow:0 6px 24px rgba(0,0,0,0.14);
  border-radius:2px;
}
.page:last-child{ page-break-after:auto; }
.page:first-child{ margin-top:32px; }

/* Print: cada .page (297×210mm) ocupa EXACTAMENTE una hoja A4 landscape.
   Reset bulletproof — anulamos absolutamente todo lo que pueda agregar
   altura/ancho fuera de los 210mm y empujar a una hoja extra. */
@media print {
  html, body{
    background:#fff !important;
    margin:0 !important;
    padding:0 !important;
    width:auto !important;
  }
  .page,
  .page:first-child,
  .page:last-child{
    margin:0 !important;
    /* Padding asimétrico con 20mm bottom para safe-area de inyección */
    padding:12mm 15mm 20mm 15mm !important;
    box-shadow:none !important;
    border-radius:0 !important;
    width:297mm !important;
    height:210mm !important;
    min-height:210mm !important;
    overflow:hidden !important;
  }
}

/* Mobile/tablet: la hoja A4 (297mm ≈ 1123px) es más ancha que el viewport.
   Habilitamos scroll horizontal en lugar de escalar — el escalado con
   transform rompe el flujo y deja huecos verticales raros. El usuario
   puede hacer pinch-zoom si quiere ver más chico. */
@media (max-width: 1200px) {
  html{ overflow-x:auto; }
  body{ min-width:297mm; }
}

.page-front{
  background-color:var(--cream);
  background-image: radial-gradient(rgba(120,90,40,0.18) 0.6px, transparent 0.6px);
  background-size: 22px 22px;
}
.page-back{ background:var(--paper-white); }

.watermark{
  position:absolute; top:50%; left:50%;
  transform: translate(-50%,-50%) rotate(-10deg);
  width:520px; opacity:0.07; pointer-events:none; z-index:0;
  mix-blend-mode:multiply; filter:grayscale(1) contrast(1.05);
}
.layer{ position:relative; z-index:10; display:flex; flex-direction:column; flex:1; }

.hd{
  display:flex; justify-content:space-between; align-items:flex-start;
  padding-bottom:14px; border-bottom:2px solid rgba(20,23,28,0.08);
}
.hd .logo img{ height:54px; width:auto; display:block; }
.hd-right{ display:flex; flex-direction:column; align-items:flex-end; gap:8px; }
.cert-no{
  background:rgba(255,255,255,0.6); border:1px solid var(--line);
  padding:8px 14px; border-radius:5px; text-align:right;
}
.cert-no .k{
  font-size:8.5pt; font-weight:700; color:var(--muted);
  letter-spacing:0.18em; text-transform:uppercase;
}
.cert-no .v{
  font-family:'SF Mono','Courier New',monospace;
  font-size:13pt; font-weight:700; color:var(--ink);
  letter-spacing:0.04em; margin-top:3px;
}
/* Bloque QR — integrado al lenguaje del cert.
   - Card matching del cert-no (mismo bg translúcido, line, radius)
   - Color de módulos = --ink (mismo del texto, no pure black "stamp")
   - Fondo del SVG = cream (el papel del cert) → se funde sin halo blanco
   - Caption con punto naranja brand + tipografía consistente */
.qr-verify{
  display:flex; flex-direction:column; align-items:flex-end; gap:6px;
  margin-top:4px;
}
.qr-card{
  background:rgba(255,255,255,0.6);
  border:1px solid var(--line);
  border-radius:5px;
  padding:7px;
  line-height:0;
}
.qr-card svg{ display:block; }
.qr-verify .cap{
  display:inline-flex; align-items:center; gap:5px;
  font-size:6.5pt; color:var(--muted);
  letter-spacing:0.08em; text-transform:lowercase;
  font-variant-numeric:tabular-nums;
}
.qr-verify .cap::before{
  content:''; width:4px; height:4px; border-radius:50%;
  background:var(--brand,#FF5B04);
}

.hero{ margin-top:18px; flex:1; display:flex; flex-direction:column; justify-content:flex-start; }
.title h1{
  margin:0; font-size:36pt; font-weight:800; color:var(--ink);
  letter-spacing:-0.015em; line-height:1; text-transform:uppercase;
}
.title .sub{
  margin:6px 0 0; font-size:14pt; font-style:italic; font-weight:600;
  color:var(--brand); letter-spacing:0.01em;
}
.says{
  margin-top:22px; font-size:16pt; color:var(--ink); font-weight:400;
  line-height:1.45;
}
.says .name{
  font-weight:700; text-transform:uppercase;
  border-bottom:1.5px solid var(--ink); padding-bottom:1px;
}
.ids{
  margin-top:6px; font-size:10pt; font-style:italic; color:var(--muted);
}

.data{
  margin-top:22px;
  border:1px solid var(--line); border-radius:8px; overflow:hidden;
  background:rgba(255,255,255,0.5);
}
.row-2{ display:grid; grid-template-columns:1.45fr 1fr; }
.row-3{ display:grid; grid-template-columns:1fr 1fr 1fr; }
.cell{
  padding:14px 18px;
  border-bottom:1px solid var(--line);
  border-right:1px solid var(--line);
}
.row-2 > .cell:last-child{ border-right:none; background:rgba(255,91,4,0.05); }
.row-3 > .cell:last-child{ border-right:none; background:rgba(20,23,28,0.025); }
.row-rend{
  padding:13px 18px; display:flex; justify-content:space-between; align-items:center;
  background:rgba(20,23,28,0.04);
}

.k{
  font-size:8.5pt; font-weight:700; color:var(--muted);
  letter-spacing:0.13em; text-transform:uppercase;
}
.v-big{ font-size:30pt; font-weight:800; color:var(--ink); margin-top:3px; line-height:1.1; }
.v-tasa{ font-size:26pt; font-weight:800; color:var(--brand); margin-top:3px; line-height:1.1; }
.v-tasa-tag{
  font-size:8.5pt; font-weight:700; color:var(--brand);
  letter-spacing:0.12em; text-transform:uppercase; margin-top:5px;
}
.v-date{ font-size:13pt; font-weight:700; color:var(--ink); margin-top:3px; }
.v-plazo{ font-size:13pt; font-weight:700; color:var(--ink); margin-top:3px; }
.rend-l{ display:flex; flex-direction:column; gap:2px; }
.rend-note{ font-size:9.5pt; color:var(--ink-soft); font-style:italic; }
.rend-amount{ font-size:16pt; font-weight:700; color:var(--ink); }
.rend-amount .unit{ font-size:10pt; font-weight:400; color:var(--muted); }

.sigs{
  display:grid; grid-template-columns:1fr auto 1fr; align-items:flex-end;
  gap:24px; margin-top:24px; position:relative; z-index:30;
}
.sig{ text-align:center; padding-top:18px; }
.sig .line{ border-top:1.2px solid var(--ink); padding-top:5px; }
.sig .name{
  font-size:9.5pt; font-weight:700; color:var(--ink);
  text-transform:uppercase; letter-spacing:0.04em;
}
.sig .role{
  font-size:8pt; font-weight:700; color:var(--muted);
  letter-spacing:0.18em; text-transform:uppercase; margin-top:2px;
}
.seal-wrap{
  width:130px; height:130px; margin:0 8px -22px; position:relative; z-index:40;
}
.seal-wrap img{
  width:100%; height:100%; object-fit:contain;
  mix-blend-mode:multiply;
  filter: drop-shadow(0 6px 14px rgba(0,0,0,0.18));
}

.legal{
  margin-top:18px; padding-top:10px;
  border-top:1px solid rgba(229,231,235,0.6);
  text-align:center; font-size:8pt; font-weight:700; color:var(--muted);
  letter-spacing:0.14em; text-transform:uppercase;
}

/* ====== PÁGINA 2 ====== */
.hd2{
  display:flex; justify-content:space-between; align-items:flex-end;
  padding-bottom:14px; border-bottom:2px solid var(--line-soft);
}
.hd2 h3{
  margin:0; font-size:15pt; font-weight:700; color:var(--ink);
  letter-spacing:0.01em; text-transform:uppercase;
}
.hd2 .anex{
  font-size:8.5pt; color:var(--brand); letter-spacing:0.18em;
  text-transform:uppercase; font-weight:700; margin-top:3px;
}
.hd2 .wm-tag{
  font-size:18pt; font-weight:900; font-style:italic;
  color:var(--line); letter-spacing:-0.02em;
}

.cols2{ display:grid; grid-template-columns:1fr 1fr; gap:36px; margin-top:22px; flex:1; }
.cols2 > div{ display:flex; flex-direction:column; }

.sec-title{
  font-size:10pt; font-weight:800; color:var(--brand);
  letter-spacing:0.18em; text-transform:uppercase; margin-bottom:10px;
}

.bens{ border:1px solid var(--line); border-radius:5px; overflow:hidden; }
.bens-head{
  display:grid; grid-template-columns:1fr 60px;
  padding:8px 14px; background:#f9fafb;
  font-size:9pt; font-weight:700; color:var(--ink); letter-spacing:0.02em;
}
.bens-head .pct{ text-align:center; }
.bens-row{
  display:grid; grid-template-columns:1fr 60px;
  padding:9px 14px; border-top:1px solid var(--line-soft);
  font-size:9.5pt; color:var(--ink-soft);
}
.bens-row .pct{ text-align:center; font-weight:700; color:var(--ink); }
.bens-row.empty{ color:transparent; }

.entity{
  margin-top:18px; padding:14px 16px;
  background:#f9fafb; border:1px solid var(--line); border-radius:5px;
}
.entity h4{
  margin:0 0 8px; font-size:9pt; font-weight:800; color:var(--ink);
  letter-spacing:0.18em; text-transform:uppercase;
}
.entity p{ margin:0 0 4px; font-size:9pt; color:var(--ink-soft); line-height:1.55; }
.entity p.addr{
  margin-top:8px; padding-top:8px;
  border-top:1px solid var(--line-soft);
}
.entity strong{ color:var(--ink); font-weight:700; }

.cond{
  font-size:9pt; color:var(--ink-soft); line-height:1.6;
  text-align:justify;
}
.cond p{ margin:0 0 8px; }
.cond strong{ color:var(--ink); font-weight:700; }
.cond .head{ color:var(--brand); }

.firma-conf{
  margin-top:auto; padding-top:48px;
  align-self:center; text-align:center;
}
.firma-conf .ln{
  border-bottom:1px solid var(--ink); width:220px; margin:0 auto 6px;
}
.firma-conf p{
  margin:0; font-size:8.5pt; font-weight:700; color:var(--ink);
  letter-spacing:0.18em; text-transform:uppercase;
}

.page-foot{
  margin-top:auto; padding-top:12px;
  border-top:1px solid var(--line-soft); text-align:center;
  font-size:8pt; color:var(--muted); letter-spacing:0.16em; text-transform:uppercase;
}
`

/**
 * Certificado de Inversión — Depósito a Plazo Fijo.
 *
 * Réplica del PDF de referencia. A4 landscape (297×210mm), tipografía Inter,
 * brand orange `#FF5B04`, sello dorado oficial + watermark. Textos
 * alineados con la spec del producto (interés simple, retiro anticipado
 * recalculado al 4% — no penalización 10/25%).
 *
 * Modality-aware (MENSUAL vs AL_VENCIMIENTO) en subtítulo, etiqueta de
 * capitalización, bloque de rendimiento y la 2da condición del reverso.
 */
export function certificadoPlazoFijoHtml(
  asociado: CertAsociado | null,
  pf: CertPlazoFijo,
): string {
  const esMensual = pf.manejoCapitalizacion === 'MENSUAL'
  const tasaPct = `${(pf.tasaAnual * 100).toFixed(2)}%`
  const meses = PLAZO_MESES_NUM[pf.plazoDias] ?? Math.round(pf.plazoDias / 30)
  const plazoTxt = `${meses} Meses`
  const subtitulo = esMensual
    ? 'Depósito a Plazo Fijo - Capitalizable'
    : 'Depósito a Plazo Fijo - Pago al Vencimiento'
  const capTag = esMensual ? 'Capitalización Mensual' : 'Pago al Vencimiento'

  const rendPorMes = pf.rendimientoBruto / meses
  const rendLabel = esMensual
    ? 'Rendimiento Mensual Estimado'
    : 'Rendimiento Total al Vencimiento'
  const rendNota = esMensual
    ? 'El interés se deposita en tu cuenta de ahorro corriente cada mes'
    : 'Capital e intereses se entregan en una sola operación al vencimiento'
  const rendValor = esMensual
    ? `${fmtQ(rendPorMes)} <span class="unit">/ mes aprox.</span>`
    : fmtQ(pf.rendimientoBruto)

  const nombre = (asociado?.nombreCompleto ?? '—').toUpperCase()
  const noAsoc = asociado?.noAsociado ?? '—'
  const dpi = asociado?.dpi ?? '—'

  const bensRows = pf.beneficiarios && pf.beneficiarios.length > 0
    ? pf.beneficiarios
        .map(
          (b) =>
            `<div class="bens-row"><div>${esc(
              b.nombreCompleto,
            )}</div><div class="pct">${b.porcentaje.toFixed(0)}%</div></div>`,
        )
        .join('')
    : Array.from({ length: 4 })
        .map(
          () =>
            `<div class="bens-row empty"><div>·</div><div class="pct">·</div></div>`,
        )
        .join('')

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>${CERT_CSS}</style></head><body>

<section class="page page-front">
  <img class="watermark" src="${SEAL_GOLD_PNG}" alt=""/>
  <div class="layer">
    <div class="hd">
      <div class="logo"><img src="${LOGO_HORIZONTAL}" alt="neocoop"/></div>
      <div class="hd-right">
        <div class="cert-no">
          <div class="k">Certificado Único No.</div>
          <div class="v">${esc(pf.cuentaId)}</div>
        </div>
        <div class="qr-verify">
          <div class="qr-card">
            ${renderQrSvg({
              content: `https://neocoop.gt/c/${pf.cuentaId}/${pf.verificationCode}`,
              ecl: 'M',
              margin: 4,
              size: 80,
              // Color = ink del cert (no pure black) y bg = cream del papel
              // → el QR se siente parte del documento, no un sticker negro
              color: '#14171c',
              background: '#fffbf2',
            })}
          </div>
          <div class="cap">verificar · neocoop.gt</div>
        </div>
      </div>
    </div>

    <div class="hero">
      <div class="title">
        <h1>Certificado de Inversión</h1>
        <p class="sub">${esc(subtitulo)}</p>
      </div>

      <div class="says">
        Se hace constar que <span class="name">${esc(nombre)}</span>
      </div>
      <div class="ids">No. Asociado: ${esc(noAsoc)} • DPI: ${esc(dpi)}</div>

      <div class="data">
        <div class="row-2">
          <div class="cell">
            <div class="k">Monto de Inversión</div>
            <div class="v-big">${fmtQ(pf.capitalInicial)}</div>
          </div>
          <div class="cell">
            <div class="k">Tasa de Interés Anual</div>
            <div class="v-tasa">${esc(tasaPct)}</div>
            <div class="v-tasa-tag">${esc(capTag)}</div>
          </div>
        </div>

        <div class="row-3">
          <div class="cell">
            <div class="k">Fecha Inicio</div>
            <div class="v-date">${fmtFechaCert(pf.fechaInicio)}</div>
          </div>
          <div class="cell">
            <div class="k">Fecha Vencimiento</div>
            <div class="v-date">${fmtFechaCert(pf.fechaVencimiento)}</div>
          </div>
          <div class="cell">
            <div class="k">Plazo Total</div>
            <div class="v-plazo">${esc(plazoTxt)}</div>
          </div>
        </div>

        <div class="row-rend">
          <div class="rend-l">
            <div class="k">${esc(rendLabel)}</div>
            <div class="rend-note">${esc(rendNota)}</div>
          </div>
          <div class="rend-amount">${rendValor}</div>
        </div>
      </div>

      <div class="sigs">
        <div class="sig">
          <div class="line">
            <div class="name">${esc(nombre)}</div>
            <div class="role">Asociado</div>
          </div>
        </div>
        <div class="seal-wrap"><img src="${SEAL_GOLD_PNG}" alt=""/></div>
        <div class="sig">
          <div class="line">
            <div class="name">Ing. Hugo Duarte</div>
            <div class="role">CEO Neocoop</div>
          </div>
        </div>
      </div>

      <div class="legal">
        NEOCOOP R.L. &nbsp;|&nbsp; NIT: ${COOP_NIT} &nbsp;|&nbsp; Regulado por la Ley de Cooperativas
      </div>
    </div>
  </div>
</section>

<section class="page page-back">
  <div class="layer">
    <div class="hd2">
      <div>
        <h3>Registro de Beneficiarios y Condiciones</h3>
        <div class="anex">Anexo al Certificado No. ${esc(pf.cuentaId)}</div>
      </div>
      <div class="wm-tag">neocoop.</div>
    </div>

    <div class="cols2">
      <div>
        <div>
          <div class="sec-title">1. Beneficiarios Designados</div>
          ${
            pf.beneficiariosSource === 'asociado' &&
            pf.beneficiarios &&
            pf.beneficiarios.length > 0
              ? `<div style="font-size:8.5pt;color:var(--ink-soft);font-style:italic;margin:-6px 0 8px;">Designación del perfil del asociado (pendiente designación específica para este producto).</div>`
              : ''
          }
          <div class="bens">
            <div class="bens-head"><div>Nombre Completo</div><div class="pct">%</div></div>
            ${bensRows}
          </div>
        </div>

        <div class="entity">
          <h4>Entidad Emisora</h4>
          <p><strong>Razón Social:</strong> ${esc(COOP_NOMBRE)}</p>
          <p><strong>NIT:</strong> ${COOP_NIT} &nbsp;|&nbsp; <strong>Correo:</strong> ${COOP_EMAIL}</p>
          <p class="addr"><strong>Dirección Fiscal:</strong> ${esc(COOP_FISCAL_ADDR)}</p>
        </div>
      </div>

      <div>
        <div class="sec-title">2. Condiciones Generales</div>
        <div class="cond">
          <p><strong class="head">1. TÍTULO EJECUTIVO.</strong> <strong>·</strong> Este certificado constituye título ejecutivo para el cobro judicial de capital e intereses.</p>
          <p><strong class="head">2. ${
            esMensual ? 'INTERESES MENSUALES' : 'PAGO AL VENCIMIENTO'
          }.</strong> <strong>·</strong> ${
            esMensual
              ? 'Los intereses se calculan con tasa simple sobre el capital y se acreditan mensualmente en la cuenta de ahorro corriente del asociado. El capital del depósito permanece intacto durante todo el plazo.'
              : 'Los intereses se calculan con tasa simple sobre el capital y se acumulan hasta el vencimiento; capital e intereses se entregan en una sola operación al finalizar el plazo.'
          }</p>
          <p><strong class="head">3. RENOVACIÓN.</strong> <strong>·</strong> Renovación automática por igual plazo y modalidad si el asociado no instruye lo contrario dentro de los 5 días hábiles posteriores al vencimiento.</p>
          <p><strong class="head">4. RETIRO ANTICIPADO.</strong> <strong>·</strong> El retiro del capital antes del vencimiento implica el recálculo de los intereses devengados a la tasa vigente de ahorro corriente (4% anual). La diferencia frente a la tasa pactada no es exigible.</p>
          <p><strong class="head">5. MARCO LEGAL.</strong> <strong>·</strong> Producto regulado por la Ley General de Cooperativas (Decreto 82-78) y supervisado por INGECOP.</p>
        </div>

        <div class="firma-conf">
          <div class="ln"></div>
          <p>Firma de Conformidad</p>
        </div>
      </div>
    </div>

    <div class="page-foot">Página 2 de 2 &nbsp;|&nbsp; Reverso Legal</div>
  </div>
</section>

</body></html>`
}
